<template>
  <div v-if="puedeVer" class="app-bar-action">
    <v-badge
      overlap
      bordered
      offset-x="14"
      offset-y="14"
      :color="pendientes.length ? 'error' : 'transparent'"
      :content="String(pendientes.length)"
      :value="pendientes.length > 0"
    >
      <v-btn
        icon
        small
        class="app-bar-btn"
        title="Dudas de Susana IA"
        :loading="cargando"
        @click="abrir"
      >
        <v-icon color="deep-purple darken-1">mdi-robot-confused-outline</v-icon>
      </v-btn>
    </v-badge>

    <v-dialog v-model="dialog" max-width="560" scrollable>
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">
          <v-icon left color="deep-purple darken-1">mdi-robot-confused-outline</v-icon>
          Dudas pendientes de Susana
          <v-spacer></v-spacer>
          <v-btn icon small @click="cargar" :loading="cargando">
            <v-icon small>mdi-refresh</v-icon>
          </v-btn>
          <v-btn icon small @click="dialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0" style="max-height: 70vh;">
          <div v-if="error" class="pa-4 red--text text-caption">{{ error }}</div>
          <div v-else-if="!pendientes.length && !cargando" class="pa-6 text-center grey--text text-caption">
            No hay dudas pendientes. Cuando Susana no sepa algo, aparece aquí.
          </div>
          <v-list v-else two-line class="py-0">
            <template v-for="(h, i) in pendientes">
              <v-list-item :key="'h-' + h.id" class="align-start">
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 font-weight-medium wrap-text">
                    {{ h.pregunta || h.motivo || 'Duda sin texto' }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption wrap-text">
                    Lead {{ h.peer || '—' }} · #{{ h.id }} · {{ formatearFecha(h.creado) }}
                  </v-list-item-subtitle>
                  <v-textarea
                    v-model="borradores[h.id]"
                    class="mt-2"
                    filled
                    dense
                    hide-details
                    rows="3"
                    auto-grow
                    placeholder="Escribe la respuesta oficial. Se la diremos al lead y la guardaremos para la próxima."
                  ></v-textarea>
                  <div class="mt-2 d-flex align-center">
                    <v-btn
                      small
                      color="primary"
                      :loading="respondiendoId === h.id"
                      :disabled="!textoBorrador(h.id)"
                      @click="responder(h)"
                    >
                      Responder y guardar
                    </v-btn>
                    <span class="ml-2 text-caption grey--text">Se guarda para que Susana ya no vuelva a preguntar</span>
                  </div>
                </v-list-item-content>
              </v-list-item>
              <v-divider v-if="i < pendientes.length - 1" :key="'d-' + h.id"></v-divider>
            </template>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

const BASE = 'susana-v3'
/** Usuarios que pueden resolver dudas de Susana (iderp). */
const IDERPS_DUDAS = [1, 334, 568, 626]

export default {
  name: 'SusanaV3DudasAppBar',

  data: () => ({
    dialog: false,
    cargando: false,
    respondiendoId: null,
    pendientes: [],
    borradores: {},
    error: '',
    timer: null,
  }),

  computed: {
    ...mapGetters('login', ['getdatosUsuarioERPERP']),
    puedeVer() {
      const id = this.getdatosUsuarioERPERP && this.getdatosUsuarioERPERP.iderp
      return IDERPS_DUDAS.indexOf(Number(id)) !== -1
    },
  },

  watch: {
    puedeVer: {
      immediate: true,
      handler(v) {
        if (v) this.iniciarPolling()
        else this.detenerPolling()
      },
    },
  },

  beforeDestroy() {
    this.detenerPolling()
  },

  methods: {
    textoBorrador(id) {
      return String(this.borradores[id] || '').trim()
    },

    formatearFecha(v) {
      if (!v) return ''
      const d = new Date(v)
      if (isNaN(d.getTime())) return String(v)
      return d.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })
    },

    abrir() {
      this.dialog = true
      this.cargar()
    },

    iniciarPolling() {
      this.detenerPolling()
      this.cargar(true)
      this.timer = setInterval(() => this.cargar(true), 30000)
    },

    detenerPolling() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },

    async cargar(silencioso) {
      if (!this.puedeVer) return
      if (!silencioso) this.cargando = true
      this.error = ''
      try {
        const res = await this.$http.get(BASE + '/admin/handoffs', {
          params: { estado: 'pendiente', limite: 100 },
        })
        const data = (res.data && res.data.data) || []
        this.pendientes = data.filter((h) => h.tipo === 'parcial')
        const borradores = Object.assign({}, this.borradores)
        this.pendientes.forEach((h) => {
          if (borradores[h.id] == null) borradores[h.id] = ''
        })
        this.borradores = borradores
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.error) || e.message || 'No se pudieron cargar las dudas'
        if (!silencioso) this.error = msg
      } finally {
        this.cargando = false
      }
    },

    async responder(h) {
      const respuesta = this.textoBorrador(h.id)
      if (!respuesta) return
      this.respondiendoId = h.id
      try {
        const usuario = this.getdatosUsuarioERPERP || {}
        const res = await this.$http.post(BASE + '/admin/handoffs/' + h.id + '/responder', {
          respuesta: respuesta,
          aprender: true,
          respondido_por: usuario.iderp || usuario.idusuario || null,
        })
        const aprendido = res.data && res.data.aprendido
        const envio = res.data && res.data.envio
        let mensaje = 'Respuesta enviada al lead'
        if (aprendido && aprendido.ok) mensaje += ' y guardada para la próxima'
        if (envio && envio.enviado === false && envio.motivo) mensaje += ' (aviso: ' + envio.motivo + ')'
        this.$store.commit('mostrarAlerta', { mensaje: mensaje, tipo: 'success' })
        this.$delete(this.borradores, h.id)
        await this.cargar(true)
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.error) || e.message || 'No se pudo responder'
        this.$store.commit('mostrarAlerta', { mensaje: msg, tipo: 'error' })
      } finally {
        this.respondiendoId = null
      }
    },
  },
}
</script>

<style scoped>
.wrap-text {
  white-space: normal;
}
</style>
